// The slice of a thread a model is shown, chosen so that consecutive turns
// share it.
//
// 2026-10-03, from "why are the dms listed as so expensive". Every surface
// asked the database for up to FULL_STORY_MSGS (8,000) rows, newest first, and
// believed it was sending the whole thread. It was not. PostgREST cuts any one
// response to the project's max_rows -- 1,000 here -- without an error, so the
// history was really "the newest 1,000 rows". Two things follow from that:
//
//   1. It is a silent cap. On a 9,096-row thread the model was reading 11% of
//      the conversation while every comment and log line said "whole story".
//   2. It SLIDES. Each new message pushes one more row out of the front of the
//      window, so the first message of the history is different on every turn.
//      Providers cache a prompt by its prefix; with the first history message
//      changing every time, nothing after the system prompt could ever be
//      reused. Replayed over that real thread, the history shared with the
//      previous turn was 0.0%. That history is ~57k tokens, re-billed at the
//      full input price on every single message.
//
// The fix is not to send less. It is to stop the window's START from moving.
// The start is rounded down to a step, so it stays put for a couple of hundred
// rows at a time and consecutive turns send the same prefix: 99.7% reusable on
// the same thread, at the same depth, and cached input bills at about 6% of
// fresh. A thread that fits in one response is sent whole, exactly as before.
//
// The window is 800-999 rows instead of always 1,000. That is the whole cost of
// the change, and what falls outside it is what the rolling summary covers.

// What one PostgREST response will actually return for this project. Asking for
// more is not an error; the extra rows are simply not there.
export const API_MAX_ROWS = 1000;
// The window never holds fewer rows than this once a thread outgrows a response.
export const HISTORY_KEEP = 800;
// How far the start jumps when it finally moves. Larger = a cold cache less
// often and a smaller minimum window; KEEP + STEP must not exceed API_MAX_ROWS.
export const HISTORY_STEP = 200;

/**
 * Index of the first row to send for a thread of `total` rows.
 * 0 while the thread fits in one response; after that it only ever moves in
 * whole steps, so it is identical from one turn to the next.
 */
export function steppedWindowStart(total, keep = HISTORY_KEEP, step = HISTORY_STEP) {
  const n = Math.max(0, Math.floor(Number(total) || 0));
  if (n < keep + step) return 0;
  return Math.floor((n - keep) / step) * step;
}

/**
 * Fetch a thread's stepped window, oldest first.
 *
 * `scope` receives a query builder and must return it with the thread's filters
 * applied; it is called twice (count, then rows) so both see the same thread.
 * Falls back to the plain newest-rows query if the count is unavailable, so a
 * failed count costs the cache benefit for that turn and nothing else.
 *
 * @returns {Promise<{data: object[]|null, error: object|null, start?: number, total?: number}>}
 */
export async function fetchSteppedWindow(supabase, table, scope) {
  const apply = typeof scope === 'function' ? scope : (q) => q;
  let total = null;
  try {
    const { count, error } = await apply(supabase.from(table).select('id', { count: 'exact', head: true }));
    if (!error && typeof count === 'number') total = count;
  } catch { /* fall through to the plain query */ }

  if (total === null) {
    const { data, error } = await apply(supabase.from(table).select('*'))
      .order('created_at', { ascending: false })
      .limit(API_MAX_ROWS);
    return { data: data ? [...data].reverse() : data, error: error || null };
  }

  const start = steppedWindowStart(total);
  const { data, error } = await apply(supabase.from(table).select('*'))
    .order('created_at', { ascending: true })
    .order('id', { ascending: true }) // a tie in created_at must not reorder the prefix
    .range(start, start + API_MAX_ROWS - 1);
  return { data, error: error || null, start, total };
}
